import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  MenuItem, 
  Paper,
  Autocomplete,
  Tooltip,
  IconButton,
  CircularProgress,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import RiskMatrix from './components/RiskMatrix';
import DepartmentSelect from './components/DepartmentSelect';
import api from '../../services/api';

const AddRisk = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [feedback, setFeedback] = useState({ open: false, title: '', message: '', severity: 'info' });

  const [metadata, setMetadata] = useState({
    nextSn: '',
    categories: [],
    departments: [],
    owners: [],
    campuses: [],
    strategic_kras: []
  });

  const [formData, setFormData] = useState({
    sn: '',
    date_reviewed: new Date().toISOString().split('T')[0],
    risk_description: '',
    kra_at_risk: '',
    causes: '',
    consequence: '',
    category: '',
    campus: '',
    strategic_kra_ids: [],
    inherent_likelihood: 'POSSIBLE',
    inherent_consequence: 'MODERATE',
    likelihood_justification: '',
    consequence_justification: '',
    existing_controls: '',
    control_effectiveness: 'MODERATE',
    residual_likelihood: 'UNLIKELY',
    residual_consequence: 'MODERATE',
    mitigation_strategy: 'Reduce',
    action_treatment: '',
    owner: '',
    resolved_by: '',
    method: 'Risk Monitoring',
    department_id: '',
    sub_department_id: '',
    status: 'Open'
  });

  useEffect(() => {
    fetchMetadata();
  }, []);

  const fetchMetadata = async () => {
    try {
      const response = await api.get('/risk-metadata');
      const data = response.data;
      setMetadata(data);
      setFormData(prev => ({ ...prev, sn: data.nextSn }));
    } catch (error) {
      console.error('Error fetching risk metadata:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const minLength = 2;

    // Helper to check length
    const isTooShort = (val) => val && val.trim().length > 0 && val.trim().length < minLength;

    if (!formData.sn?.trim()) newErrors.sn = 'Risk ID is required';
    else if (isTooShort(formData.sn)) newErrors.sn = `Risk ID must be at least ${minLength} characters`;

    if (formData.date_reviewed) {
        const selectedDate = new Date(formData.date_reviewed);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (selectedDate > today) {
            newErrors.date_reviewed = 'Review date cannot be in the future';
        }
    }

    if (formData.resolved_by) {
        const selectedDate = new Date(formData.resolved_by);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Allow today
        if (selectedDate < today) {
            newErrors.resolved_by = 'TARGET RESOLUTION cannot be in the past';
        }
    }

    if (!formData.category?.trim()) newErrors.category = 'Category is required';
    else if (isTooShort(formData.category)) newErrors.category = `Category must be at least ${minLength} characters`;

    if (!formData.risk_description?.trim()) newErrors.risk_description = 'Description is required';
    else if (isTooShort(formData.risk_description)) newErrors.risk_description = `Description must be at least ${minLength} characters`;

    if (!formData.owner?.trim()) newErrors.owner = 'Risk Owner is required';
    else if (isTooShort(formData.owner)) newErrors.owner = `Risk Owner must be at least ${minLength} characters`;

    if (!formData.department_id?.trim()) newErrors.department_id = 'Department is required';
    if (!formData.campus?.trim()) newErrors.campus = 'Campus is required';

    // Optional fields validation
    ['kra_at_risk', 'causes', 'consequence', 'existing_controls', 'action_treatment', 'resolved_by', 'method'].forEach(field => {
        if (isTooShort(formData[field])) {
            newErrors[field] = `Entry must be at least ${minLength} characters`;
        }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getRatingInfo = (likelihood, consequence) => {
    const likelihoodMap = { 'RARE': 0, 'UNLIKELY': 1, 'POSSIBLE': 2, 'LIKELY': 3, 'ALMOST CERTAIN': 4 };
    const matrix = {
      'CATASTROPHIC': ['TOLERABLE', 'HIGH', 'EXTREME', 'EXTREME', 'EXTREME'],
      'MAJOR': ['LOW', 'TOLERABLE', 'HIGH', 'EXTREME', 'EXTREME'],
      'MODERATE': ['LOW', 'LOW', 'TOLERABLE', 'HIGH', 'HIGH'],
      'MINOR': ['VERY LOW', 'LOW', 'TOLERABLE', 'TOLERABLE', 'HIGH'],
      'INSIGNIFICANT': ['VERY LOW', 'VERY LOW', 'LOW', 'TOLERABLE', 'TOLERABLE']
    };
    const colors = {
      'EXTREME': '#ef4444',
      'HIGH': '#f97316',
      'TOLERABLE': '#eab308',
      'LOW': '#84cc16',
      'VERY LOW': '#15803d'
    };
    const idx = likelihoodMap[likelihood];
    const label = matrix[consequence]?.[idx] || '---';
    return { label, color: colors[label] || '#94a3b8' };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Qualitative text labels are used for all fields including assessments now
    const updatedForm = { ...formData, [name]: value };
    
    setFormData(updatedForm);
    if (errors[name]) {
        setErrors(prev => {
            const updated = { ...prev };
            delete updated[name];
            return updated;
        });
    }
  };

  const handleAutocompleteChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'department_id' && value) {
      refreshRiskId(value);
    }
    if (errors[name]) {
        setErrors(prev => {
            const updated = { ...prev };
            delete updated[name];
            return updated;
        });
    }
  };

  const refreshRiskId = async (departmentId) => {
    try {
      const response = await api.get('/risk-metadata', { params: { department_id: departmentId } });
      if (response.data?.nextSn) {
        setFormData(prev => ({ ...prev, sn: response.data.nextSn }));
      }
    } catch (error) {
      console.error('Error refreshing risk ID:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
        setFeedback({ 
            open: true, 
            title: 'Validation Error', 
            message: 'Please correct validation errors before committing.', 
            severity: 'error' 
        });
        return;
    }

    setSaving(true);
    try {
      // Calculate scores before sending
      const likelihoodMap = { 'RARE': 1, 'UNLIKELY': 2, 'POSSIBLE': 3, 'LIKELY': 4, 'ALMOST CERTAIN': 5 };
      const consequenceMap = { 'INSIGNIFICANT': 1, 'MINOR': 2, 'MODERATE': 3, 'MAJOR': 4, 'CATASTROPHIC': 5 };
      
      const payload = {
        ...formData,
        inherent_risk_score: (likelihoodMap[formData.inherent_likelihood] || 3) * (consequenceMap[formData.inherent_consequence] || 3),
        residual_risk_score: (likelihoodMap[formData.residual_likelihood] || 3) * (consequenceMap[formData.residual_consequence] || 3)
      };

      await api.post('/risks', payload);
      setFeedback({ 
          open: true, 
          title: 'Success!', 
          message: 'Risk record committed successfully.', 
          severity: 'success' 
      });
    } catch (error) {
      console.error('Error saving risk:', error);
      setFeedback({ 
          open: true, 
          title: error.response ? 'Submission Failed' : 'Connection Error', 
          message: error.response?.data?.error || 'Failed to commit record. System sync error.', 
          severity: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseFeedback = () => {
    const isSuccess = feedback.severity === 'success';
    setFeedback(prev => ({ ...prev, open: false }));
    if (isSuccess) {
      onBack();
    }
  };

  const likelihoodOptions = [
    { value: 'RARE', label: 'RARE' },
    { value: 'UNLIKELY', label: 'UNLIKELY' },
    { value: 'POSSIBLE', label: 'POSSIBLE' },
    { value: 'LIKELY', label: 'LIKELY' },
    { value: 'ALMOST CERTAIN', label: 'ALMOST CERTAIN' }
  ];

  const consequenceOptions = [
    { value: 'INSIGNIFICANT', label: 'INSIGNIFICANT' },
    { value: 'MINOR', label: 'MINOR' },
    { value: 'MODERATE', label: 'MODERATE' },
    { value: 'MAJOR', label: 'MAJOR' },
    { value: 'CATASTROPHIC', label: 'CATASTROPHIC' }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#10b981' }} />
      </Box>
    );
  }

  const SectionTitle = ({ title, color }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
        <Typography variant="h6" sx={{ color: color, fontWeight: 'bold', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
            {title}
        </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 4, maxWidth: '1400px', margin: '0 auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={onBack} sx={{ color: 'white', mr: 2, background: 'rgba(255,255,255,0.05)', '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" className="gradient-text" sx={{ fontWeight: 'bold' }}>
          Risk Entry
        </Typography>
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: '12px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
            {submitError}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            {/* Section 1: Risk Identification */}
            <Paper className="glass" sx={{ p: 4, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', mb: 4 }}>
              <SectionTitle title="RISK IDENTIFICATION" color="#3b82f6" />
              <Divider sx={{ mb: 3, borderColor: 'rgba(59, 130, 246, 0.2)' }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={2}>
                  <TextField fullWidth required label="Risk ID" name="sn" value={formData.sn} onChange={handleInputChange} error={!!errors.sn} helperText={errors.sn} sx={{ input: { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField fullWidth type="date" label="Date Reviewed" name="date_reviewed" value={formData.date_reviewed} onChange={handleInputChange} InputLabelProps={{ shrink: true }} inputProps={{ max: new Date().toISOString().split('T')[0] }} error={!!errors.date_reviewed} helperText={errors.date_reviewed} sx={{ input: { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete freeSolo options={metadata.categories} value={formData.category} onChange={(e, val) => handleAutocompleteChange('category', val)} onInputChange={(e, val) => handleAutocompleteChange('category', val)} renderInput={(params) => (
                    <TextField {...params} required label="Risk Category" error={!!errors.category} helperText={errors.category} sx={{ input: { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }} />
                  )} />
                </Grid>
                <DepartmentSelect
                  departmentId={formData.department_id}
                  subDepartmentId={formData.sub_department_id}
                  onDepartmentChange={(id, name) => {
                    setFormData(prev => ({ ...prev, department_id: id, sub_department_id: '' }));
                    if (id) refreshRiskId(id);
                    if (errors.department_id) setErrors(prev => ({ ...prev, department_id: '' }));
                  }}
                  onSubDepartmentChange={(id) => setFormData(prev => ({ ...prev, sub_department_id: id }))}
                  required
                  deptError={errors.department_id}
                  fieldSx={{ input: { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' }, '& .MuiSelect-select': { color: 'white' }, '& .MuiSvgIcon-root': { color: '#94a3b8' } }}
                  menuProps={{ PaperProps: { sx: { background: '#1e293b', color: 'white', maxHeight: 320, '& .MuiMenuItem-root:hover': { background: 'rgba(255,255,255,0.08)' }, '& .MuiMenuItem-root.Mui-disabled': { opacity: 1, color: '#64748b' } } } }}
                />
                <Grid item xs={12} md={6}>
                  <TextField fullWidth required multiline rows={3} label="Risk Description" name="risk_description" value={formData.risk_description} onChange={handleInputChange} error={!!errors.risk_description} helperText={errors.risk_description} sx={{ textarea: { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth required select label="Campus" name="campus" value={formData.campus} onChange={handleInputChange} error={!!errors.campus} helperText={errors.campus} sx={{ '& .MuiSelect-select': { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }}>
                    {(metadata.campuses || []).map(campus => <MenuItem key={campus} value={campus}>{campus}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete multiple options={metadata.strategic_kras || []} getOptionLabel={(option) => option?.name ? `${option.code} - ${option.name}` : ''} value={(metadata.strategic_kras || []).filter(kra => formData.strategic_kra_ids.includes(kra.id))} onChange={(e, val) => handleAutocompleteChange('strategic_kra_ids', val.map(kra => kra.id))} renderInput={(params) => (
                    <TextField {...params} label="Strategic KRA" helperText="Optional strategic alignment" sx={{ input: { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }} />
                  )} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth multiline rows={2} label="Cause" name="causes" value={formData.causes} onChange={handleInputChange} error={!!errors.causes} helperText={errors.causes} sx={{ textarea: { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth multiline rows={2} label="Consequence" name="consequence" value={formData.consequence} onChange={handleInputChange} error={!!errors.consequence} helperText={errors.consequence} sx={{ textarea: { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={4}>
          {/* Section 2: Inherent Assessment */}
          <Grid item xs={12}>
            <Grid container spacing={4} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper className="glass" sx={{ p: 4, background: 'rgba(15, 23, 42, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', height: '100%', mb: 2 }}>
                  <SectionTitle title="RISK ASSESSMENT (INHERENT)" color="#3b82f6" />
                  <Divider sx={{ mb: 3, borderColor: 'rgba(59, 130, 246, 0.2)' }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth select label="Likelihood" name="inherent_likelihood" value={formData.inherent_likelihood} onChange={handleInputChange} sx={{ '& .MuiSelect-select': { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }}>
                            {likelihoodOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth select label="Consequence" name="inherent_consequence" value={formData.inherent_consequence} onChange={handleInputChange} sx={{ '& .MuiSelect-select': { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }}>
                            {consequenceOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth multiline rows={2} label="Why this Likelihood rating?" name="likelihood_justification" value={formData.likelihood_justification} onChange={handleInputChange} placeholder="Briefly justify the selected likelihood..." sx={{ textarea: { color: 'white', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth multiline rows={2} label="Why this Consequence rating?" name="consequence_justification" value={formData.consequence_justification} onChange={handleInputChange} placeholder="Briefly justify the selected consequence..." sx={{ textarea: { color: 'white', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, background: `${getRatingInfo(formData.inherent_likelihood, formData.inherent_consequence).color}15`, borderRadius: '16px', textAlign: 'center', border: `1px solid ${getRatingInfo(formData.inherent_likelihood, formData.inherent_consequence).color}33` }}>
                        <Typography sx={{ color: getRatingInfo(formData.inherent_likelihood, formData.inherent_consequence).color, fontSize: '0.55rem', fontWeight: 'bold', mb: 0.5, letterSpacing: '1px' }}>INHERENT RATING</Typography>
                        <Typography sx={{ fontWeight: '900', color: getRatingInfo(formData.inherent_likelihood, formData.inherent_consequence).color, fontSize: '0.85rem' }}>{getRatingInfo(formData.inherent_likelihood, formData.inherent_consequence).label}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper className="glass" sx={{ p: 4, background: 'rgba(15, 23, 42, 0.2)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', mb: 2 }}>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <RiskMatrix selectedLikelihood={formData.inherent_likelihood} selectedConsequence={formData.inherent_consequence} title="Inherent Risk Matrix" />
                    </Box>
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* Section 3: Residual Assessment */}
          <Grid item xs={12}>
            <Grid container spacing={4} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper className="glass" sx={{ p: 4, background: 'rgba(15, 23, 42, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', height: '100%', mb: 2 }}>
                  <SectionTitle title="RISK ASSESSMENT (RESIDUAL)" color="#3b82f6" />
                  <Divider sx={{ mb: 3, borderColor: 'rgba(59, 130, 246, 0.2)' }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Existing Controls" name="existing_controls" value={formData.existing_controls} onChange={handleInputChange} error={!!errors.existing_controls} helperText={errors.existing_controls} sx={{ input: { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth select label="Control Adequacy" name="control_effectiveness" value={formData.control_effectiveness} onChange={handleInputChange} sx={{ '& .MuiSelect-select': { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }}>
                        <MenuItem value="Strong">Strong</MenuItem>
                        <MenuItem value="Effective">Effective</MenuItem>
                        <MenuItem value="Moderate">Moderate</MenuItem>
                        <MenuItem value="Weak">Weak</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth select label="Likelihood" name="residual_likelihood" value={formData.residual_likelihood} onChange={handleInputChange} sx={{ '& .MuiSelect-select': { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }}>
                            {likelihoodOptions.map(opt => <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.85rem' }}>{opt.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth select label="Consequence" name="residual_consequence" value={formData.residual_consequence} onChange={handleInputChange} sx={{ '& .MuiSelect-select': { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }}>
                            {consequenceOptions.map(opt => <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.85rem' }}>{opt.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ p: 1.5, background: `${getRatingInfo(formData.residual_likelihood, formData.residual_consequence).color}15`, borderRadius: '16px', textAlign: 'center', border: `1px solid ${getRatingInfo(formData.residual_likelihood, formData.residual_consequence).color}33` }}>
                        <Typography sx={{ color: getRatingInfo(formData.residual_likelihood, formData.residual_consequence).color, fontSize: '0.55rem', fontWeight: 'bold', mb: 0.5, letterSpacing: '1px' }}>RESIDUAL RATING</Typography>
                        <Typography sx={{ fontWeight: '900', color: getRatingInfo(formData.residual_likelihood, formData.residual_consequence).color, fontSize: '0.85rem' }}>{getRatingInfo(formData.residual_likelihood, formData.residual_consequence).label}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper className="glass" sx={{ p: 4, background: 'rgba(15, 23, 42, 0.2)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', mb: 2 }}>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <RiskMatrix selectedLikelihood={formData.residual_likelihood} selectedConsequence={formData.residual_consequence} title="Residual Risk Matrix" />
                    </Box>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

            {/* Paired Treatment & Monitoring Section */}
            <Grid container spacing={4} sx={{ my: 2 }} alignItems="stretch">
                <Grid item xs={12} md={6}>
                    <Paper className="glass" sx={{ p: 4, background: 'rgba(30, 41, 59, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', height: '100%' }}>
                        <SectionTitle title="RISK TREATMENT" color="#3b82f6" />
                        <Divider sx={{ mb: 3, borderColor: 'rgba(59, 130, 246, 0.2)' }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth select label="Action" name="mitigation_strategy" value={formData.mitigation_strategy} onChange={handleInputChange} sx={{ '& .MuiSelect-select': { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }}>
                                    <MenuItem value="Reduce">Reduce</MenuItem>
                                    <MenuItem value="Accept">Accept</MenuItem>
                                    <MenuItem value="Share">Share</MenuItem>
                                    <MenuItem value="Avoid">Avoid</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <TextField fullWidth multiline rows={2} label="Plan" name="action_treatment" value={formData.action_treatment} onChange={handleInputChange} error={!!errors.action_treatment} helperText={errors.action_treatment} sx={{ textarea: { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }} />
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete freeSolo options={metadata.owners} value={formData.owner} onChange={(e, val) => handleAutocompleteChange('owner', val)} onInputChange={(e, val) => handleAutocompleteChange('owner', val)} renderInput={(params) => (
                                    <TextField {...params} required label="Risk Owner" error={!!errors.owner} helperText={errors.owner} sx={{ input: { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }} />
                                )} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField 
                                    fullWidth 
                                    type="date" 
                                    label="TARGET RESOLUTION" 
                                    name="resolved_by" 
                                    value={formData.resolved_by} 
                                    onChange={handleInputChange} 
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: new Date().toISOString().split('T')[0] }}
                                    error={!!errors.resolved_by} 
                                    helperText={errors.resolved_by} 
                                    sx={{ input: { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }} 
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper className="glass" sx={{ p: 4, background: 'rgba(15, 23, 42, 0.4)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', height: '100%' }}>
                        <SectionTitle title="RISK MONITORING & REVIEW" color="#3b82f6" />
                        <Divider sx={{ mb: 3, borderColor: 'rgba(59, 130, 246, 0.2)' }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Method" name="method" value={formData.method} onChange={handleInputChange} error={!!errors.method} helperText={errors.method} sx={{ input: { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth select label="Status" name="status" value={formData.status} onChange={handleInputChange} sx={{ '& .MuiSelect-select': { color: 'white', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.7rem' }, label: { color: '#94a3b8' } }}>
                                    <MenuItem value="Open">Open</MenuItem>
                                    <MenuItem value="In Progress">In Progress</MenuItem>
                                    <MenuItem value="Resolved">Resolved</MenuItem>
                                    <MenuItem value="Closed">Closed</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            <Button type="submit" fullWidth variant="contained" startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />} disabled={saving} sx={{ mt: 2, borderRadius: '12px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', textTransform: 'none', py: 2, fontSize: '1.2rem', fontWeight: 'bold', boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.39)', '&.Mui-disabled': { background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', opacity: 0.8 } }}>
              {saving ? 'Saving...' : 'Commit Risk Record'}
            </Button>
          </Grid>
        </form>

      {/* Feedback Dialog */}
      <Dialog 
        open={feedback.open} 
        onClose={handleCloseFeedback}
        PaperProps={{
          sx: {
            background: 'rgba(15, 23, 42, 0.9)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            minWidth: '350px'
          }
        }}
      >
        <DialogTitle sx={{ color: feedback.severity === 'success' ? '#10b981' : '#f43f5e', fontWeight: 'bold', textAlign: 'center', pt: 4 }}>
          {feedback.title}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <DialogContentText sx={{ color: 'white', fontSize: '1.1rem' }}>
            {feedback.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 4 }}>
          <Button 
            onClick={handleCloseFeedback} 
            variant="contained" 
            sx={{ 
              background: feedback.severity === 'success' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
              borderRadius: '8px',
              px: 4,
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            {feedback.severity === 'success' ? 'Back to Register' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddRisk;
